import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { api } from '../../shared/api/apiClient';
import type {
    OrderLifetimeSettingsDto,
    SaveOrderLifetimeSettingsRequest
} from '../../types';

const DEFAULTS: SaveOrderLifetimeSettingsRequest = {
    draftLifetimeDays: 30,
    sentLifetimeDays: 90,
    completedLifetimeDays: 90,
    cancelledLifetimeDays: 30,
    archiveRetentionDays: 0
};

const OrderLifetimeSettingsPanel: React.FC = () => {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [form, setForm] = useState<SaveOrderLifetimeSettingsRequest>(DEFAULTS);

    useEffect(() => {
        let isMounted = true;
        api.get<{ settings: OrderLifetimeSettingsDto }>('/api/admin/settings/order-lifetime')
            .then(response => {
                if (!isMounted || !response.data) return;
                const settings = response.data.settings;
                setForm({
                    draftLifetimeDays: settings.draftLifetimeDays,
                    sentLifetimeDays: settings.sentLifetimeDays,
                    completedLifetimeDays: settings.completedLifetimeDays,
                    cancelledLifetimeDays: settings.cancelledLifetimeDays,
                    archiveRetentionDays: settings.archiveRetentionDays
                });
            })
            .catch(() => {})
            .finally(() => {
                if (isMounted) setLoading(false);
            });
        return () => {
            isMounted = false;
        };
    }, []);

    const updateField = (field: keyof SaveOrderLifetimeSettingsRequest, value: number) =>
        setForm(prev => ({ ...prev, [field]: value }));

    const handleSave = async () => {
        const fields: Array<keyof SaveOrderLifetimeSettingsRequest> = [
            'draftLifetimeDays',
            'sentLifetimeDays',
            'completedLifetimeDays',
            'cancelledLifetimeDays'
        ];
        for (const field of fields) {
            if (form[field] < 1) {
                toast.error('Сроки жизни должны быть не меньше 1 дня');
                return;
            }
        }
        if (form.archiveRetentionDays < 0) {
            toast.error('Срок хранения архива не может быть отрицательным');
            return;
        }

        setSaving(true);
        try {
            const response = await api.put<{ settings: OrderLifetimeSettingsDto }>(
                '/api/admin/settings/order-lifetime',
                form
            );
            if (response.data) {
                const settings = response.data.settings;
                setForm({
                    draftLifetimeDays: settings.draftLifetimeDays,
                    sentLifetimeDays: settings.sentLifetimeDays,
                    completedLifetimeDays: settings.completedLifetimeDays,
                    cancelledLifetimeDays: settings.cancelledLifetimeDays,
                    archiveRetentionDays: settings.archiveRetentionDays
                });
            }
            toast.success('Настройки сохранены');
        } catch {
            // ошибки показывает apiClient
        } finally {
            setSaving(false);
        }
    };

    const dayField = (
        id: string,
        label: string,
        icon: string,
        field: keyof SaveOrderLifetimeSettingsRequest,
        hint: string
    ) => (
        <div className="form-group-custom">
            <label className="form-label-custom" htmlFor={id}>
                <i className={`bi ${icon}`}></i> {label}
            </label>
            <input
                id={id}
                type="number"
                min={field === 'archiveRetentionDays' ? 0 : 1}
                className="form-input-custom"
                value={form[field]}
                onChange={(event) => updateField(field, Number(event.target.value))}
            />
            <small className="form-text text-muted">{hint}</small>
        </div>
    );

    return (
        <div className="settings-panel">
            <h4>
                <i className="bi bi-hourglass-split"></i> Время жизни заказов
            </h4>
            <p className="settings-panel-hint">
                Управление автоматическим архивированием и удалением заказов.
                Черновики удаляются, остальные заказы перемещаются в архив.
            </p>

            {loading ? (
                <div className="text-center py-4">
                    <span className="spinner-border spinner-border-sm"></span>
                </div>
            ) : (
                <>
                    <div className="form-grid">
                        {dayField(
                            'lifetime-draft',
                            'Черновики',
                            'bi-pencil-square',
                            'draftLifetimeDays',
                            'Через сколько дней удалять черновики (по умолчанию 30)'
                        )}
                        {dayField(
                            'lifetime-sent',
                            'Отправленные',
                            'bi-send',
                            'sentLifetimeDays',
                            'Через сколько дней архивировать отправленные заказы (по умолчанию 90)'
                        )}
                        {dayField(
                            'lifetime-completed',
                            'Завершённые',
                            'bi-check-circle',
                            'completedLifetimeDays',
                            'Через сколько дней архивировать завершённые заказы (по умолчанию 90)'
                        )}
                        {dayField(
                            'lifetime-cancelled',
                            'Отменённые',
                            'bi-x-circle',
                            'cancelledLifetimeDays',
                            'Через сколько дней архивировать отменённые заказы (по умолчанию 30)'
                        )}
                        {dayField(
                            'lifetime-archive-retention',
                            'Хранение архива',
                            'bi-archive',
                            'archiveRetentionDays',
                            'Сколько дней хранить архив (0 — бессрочно)'
                        )}
                    </div>

                    <div className="settings-actions">
                        <button
                            type="button"
                            className="btn-primary-custom"
                            onClick={() => void handleSave()}
                            disabled={saving}
                        >
                            {saving ? <span className="btn-spinner"></span> : <i className="bi bi-check-lg"></i>}
                            Сохранить
                        </button>
                    </div>
                </>
            )}
        </div>
    );
};

export default OrderLifetimeSettingsPanel;
